import { useState } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import PageSEO from "../../components/feature/PageSEO";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";

const siteUrl = (import.meta.env.VITE_SITE_URL ?? '').replace(/\/$/, '');

const contactSchemas = [
  {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact PixelCraft Agency | Get a Small Business Website Quote",
    "description": "Get in touch to discuss your small business website. We respond within 24 hours with a clear, fixed-price quote.",
    "url": `${siteUrl || "https://example.com"}/contact`,
    "inLanguage": "en-GB",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": siteUrl || "https://example.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Contact",
          "item": `${siteUrl || "https://example.com"}/contact`
        }
      ]
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "PixelCraft Agency",
    "description": "Affordable small business website design and SEO services. Get your website built for just £149.",
    "url": siteUrl || "https://example.com",
    "telephone": "+441156478686",
    "email": "hello@pixelcraft.agency",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "GB"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "17:00"
      }
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+441156478686",
      "email": "hello@pixelcraft.agency",
      "contactType": "customer service",
      "contactOption": "TollFree",
      "availableLanguage": "English"
    }
  }
];

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const { ref, visible } = useScrollAnimation(0.05);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch("https://readdy.ai/api/form/d72nifkij602egd95ltg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main>
      <PageSEO
        title="Contact Us | Get a Small Business Website Quote | PixelCraft Agency"
        description="Get in touch with PixelCraft Agency to discuss your small business website. Fixed price from £149. We respond within 24 hours. No jargon, no hidden fees."
        canonical="/contact"
        schema={contactSchemas}
      />
      <Navbar />
      <section className="pt-32 pb-20 bg-white">
        <div className="container-md">
          <nav className="text-sm text-zinc-400 mb-6 flex items-center gap-2">
            <Link to="/" className="hover:text-accent cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-zinc-700">Contact</span>
          </nav>

          <div
            ref={ref}
            className={`grid grid-cols-1 lg:grid-cols-2 gap-16 transition-all duration-700 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            {/* Left: Info */}
            <div>
              <span className="text-accent text-xs font-semibold uppercase tracking-[0.2em] mb-4 block">
                Get in touch
              </span>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-zinc-900 leading-tight mb-5">
                Let&apos;s talk about your website
              </h1>
              <p className="text-zinc-600 text-base leading-relaxed mb-8">
                Whether you need a brand new website, a redesign, or want to get found on Google — we&apos;re here to help. Fill in the form and we&apos;ll get back to you within 24 hours.
              </p>

              <div className="space-y-5 mb-10">
                {[
                  { icon: "ri-mail-line", label: "Email us", val: "weblify@mail.com" },
                  { icon: "ri-phone-line", label: "Call us", val: "01156 - 478686" },
                  { icon: "ri-time-line", label: "Response time", val: "Within 24 hours" },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-accent/10 rounded-xl shrink-0">
                      <i className={`${item.icon} text-accent text-lg`} />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-xs">{item.label}</p>
                      <p className="font-semibold text-zinc-900 text-sm">{item.val}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-stone-50 rounded-2xl p-6">
                <p className="font-semibold text-zinc-800 mb-3">What happens next?</p>
                {[
                  "We review your enquiry within 24 hours",
                  "We schedule a free 30-min discovery call",
                  "You get a clear, fixed-price quote",
                  "We build your site and launch it",
                ].map((step, i) => (
                  <div key={step} className="flex items-start gap-3 mb-3 last:mb-0">
                    <span className="w-5 h-5 flex items-center justify-center bg-accent text-white rounded-full text-xs font-bold shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <span className="text-zinc-600 text-sm">{step}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Form */}
            <div>
              {submitted ? (
                <div className="bg-stone-50 rounded-2xl p-10 text-center h-full flex flex-col items-center justify-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-green-600" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-zinc-900 mb-2">Message Sent!</h2>
                  <p className="text-zinc-500 text-sm mb-6 max-w-xs">
                    Thanks for reaching out! We&apos;ll review your enquiry and get back to you within 24 hours.
                  </p>
                  <Link
                    to="/"
                    className="text-accent font-semibold hover:underline cursor-pointer text-sm"
                  >
                    Back to Home
                  </Link>
                </div>
              ) : (
                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="bg-stone-50 rounded-2xl p-6 md:p-8 space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-1.5">Full Name *</label>
                      <input
                        type="text"
                        name="full_name"
                        required
                        className="w-full border border-zinc-200 bg-white rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-1.5">Email *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full border border-zinc-200 bg-white rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-1.5">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      className="w-full border border-zinc-200 bg-white rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
                      placeholder="+44 7700 900000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-1.5">Business Name</label>
                    <input
                      type="text"
                      name="business_name"
                      className="w-full border border-zinc-200 bg-white rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
                      placeholder="Your business"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-1.5">What do you need?</label>
                    <select
                      name="service_needed"
                      className="w-full border border-zinc-200 bg-white rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors cursor-pointer"
                    >
                      <option value="">Select a service</option>
                      <option value="new-website">Brand New Website</option>
                      <option value="redesign">Website Redesign</option>
                      <option value="seo">SEO for Small Businesses</option>
                      <option value="ecommerce">E-commerce Website</option>
                      <option value="not-sure">Not Sure Yet</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                      Tell us about your project ({charCount}/500)
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full border border-zinc-200 bg-white rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors resize-none"
                      placeholder="Tell us about your business and what you&apos;re looking for..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-accent hover:bg-accent-dark text-white font-semibold py-3.5 rounded-lg cursor-pointer transition-colors whitespace-nowrap disabled:opacity-60 text-sm"
                  >
                    {submitting ? "Sending..." : "Send Enquiry"}
                  </button>

                  <p className="text-zinc-400 text-xs text-center">
                    We&apos;ll respond within 24 hours. No spam, ever.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
